import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryComics = useMemo(() => {
    let list = comics.filter((c) => c.category === currentCategory.slug);
    if (sortBy === 'popular') return [...list].sort((a, b) => b.views - a.views);
    if (sortBy === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [currentCategory.slug, sortBy]);

  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - 韩漫画网`,
    description: `韩漫画网${currentCategory.name}漫画专区 - ${currentCategory.description}`,
    url: `https://www.hanmanhua.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'https://www.hanmanhua.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}漫画 - 韩漫画网 | 免费正版韩国${currentCategory.name}漫画在线阅读`}
        description={`韩漫画网${currentCategory.name}漫画专区，${currentCategory.description}。海量正版韩国${currentCategory.name}漫画每日更新，免费阅读，内容安全可靠。`}
        keywords={`${currentCategory.name}漫画, 韩国${currentCategory.name}漫画, 韩漫${currentCategory.name}, 免费${currentCategory.name}漫画, 韩漫画网`}
        canonicalUrl={`https://www.hanmanhua.com/category/${currentCategory.slug}`}
        ogType="website"
        schemaJson={schemaJson}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: `${currentCategory.name}漫画`, href: `/category/${currentCategory.slug}` }]} />
              <div className="pb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  {currentCategory.description} — 共 {categoryComics.length} 部作品
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    currentCategory.slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">排序：</span>
              {([
                { value: 'popular', label: '最受欢迎' },
                { value: 'latest', label: '最近更新' },
                { value: 'rating', label: '评分最高' },
              ] as const).map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSortBy(s.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === s.value
                      ? 'bg-accent-100 text-accent-700 border border-accent-300'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-transparent'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {categoryComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
                <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mt-2 inline-block cursor-pointer">返回首页</Link>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}